import { useState, useCallback, Dispatch, SetStateAction } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { setToastNotification } from '../../../store/slices/standUpSlice';
import { fetchFavorites } from '../../../store/slices/standupRedirectSlice';
import { useStandUpDashboard } from './useStandUpDashBoard';
import { useLighthouseKpiFavorite } from './useLighthouseKpiFavorite';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';

/**
 * Parameters for useSparcKpiFavorite hook
 */
interface UseSparcKpiFavoriteParams {
  setShowUnfavouriteModal: Dispatch<SetStateAction<boolean>>;
  setPendingUnfavouriteCardId: Dispatch<SetStateAction<string | null>>;
  setPendingUnfavouriteCardTitle: Dispatch<SetStateAction<string | null>>;
  setPendingUnfavouriteType: Dispatch<SetStateAction<'sparc' | 'lighthouse' | null>>;
  pendingUnfavouriteCardId: string | null;
  pendingUnfavouriteCardTitle: string | null;
  pendingUnfavouriteType: 'sparc' | 'lighthouse' | null;
  sparcCards: Array<{ id: string; title: string; [key: string]: any }>;
  lighthouseCardsData: Array<{ id: string; title: string; [key: string]: any }>;
  onToggleFavorite: (cardId: string) => void;
  onToggleLighthouseFavorite?: (cardId: string) => void;
}

interface ToggleFavoriteOptions {
  skipRefresh?: boolean;
}

/**
 * Custom hook to handle SPARC KPI favorite functionality
 * 
 * This hook provides functionality to:
 * - Toggle favorite status for SPARC KPIs
 * - Make POST request to /stand-up/favourites endpoint
 * - Handle unfavorite confirmation modal logic
 * 
 * @returns {object} Object containing toggleFavorite function, loading state, and modal handlers
 */
export const useSparcKpiFavorite = (params?: UseSparcKpiFavoriteParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  // Get data from Redux
  const sessionId = useSelector((state: RootState) => state.standupRedirect?.session?.sessionId);
  const standupType = useSelector((state: RootState) => state.standupRedirect?.session?.standupType);
  const locationId = useSelector((state: RootState) => state.appData?.selectedLocation?.id);
  const locationName = useSelector((state: RootState) => state.appData?.selectedLocation?.displayText);
  const userName =useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? ''
  );
  const favoritesData = useSelector((state: RootState) => state.standupRedirect?.favorites?.data);

  // Read isEditMode and isViewMode flags directly from Redux state
  const isEditMode = useSelector((state: RootState) => state.standupRedirect?.session?.isEditMode ?? false);
  const isViewMode = useSelector((state: RootState) => state.standupRedirect?.session?.isViewMode ?? false);
  const standupId = useSelector((state: RootState) => state.standupRedirect?.session?.standupId);

  const {transformStandupTypeForFavourites} = useStandUpDashboard();

  const refreshFavoritesList = useCallback(async (): Promise<void> => {
    if (!sessionId || !standupType || !locationId || !locationName) {
      return;
    }

    try {
      await dispatch(
        fetchFavorites({
          standupSessionId: String(sessionId),
          standupId: String(standupId),
          locationId: String(locationId),
          locationName: String(locationName),
          standupType: transformStandupTypeForFavourites(String(standupType)),
          isEditMode: Boolean(isEditMode),
          isViewMode: Boolean(isViewMode),
        })
      ).unwrap();
      console.log('✓ Successfully fetched updated favorites');
    } catch (fetchError) {
      console.error('Error fetching updated favorites:', fetchError);
    }
  }, [dispatch, sessionId, standupId, locationId, locationName, standupType, transformStandupTypeForFavourites, isEditMode, isViewMode]);

  /**
   * Toggle favorite status for a SPARC KPI
   * 
   * @param id - The unique identifier for the SPARC KPI
   * @param title - The title/name of the SPARC KPI
   * @returns Promise<boolean> - Returns true if successful, false otherwise
   */
  const toggleFavorite = async (id: string, title: string, options?: ToggleFavoriteOptions): Promise<boolean> => {
    // Validate required data
    if (!sessionId || !standupType || !locationId || !locationName || !userName) {
      console.error('Missing required data for favorite API call', {
        sessionId,
        standupType,
        locationId,
        locationName,
        userName,
      });
      return false;
    }

    setIsLoading(true);
    let currentOperation: 'add' | 'remove' = 'add'; // Track the current operation for error handling
    
    try {
      const api = createApiClient();

      // Extract the actual favorites array from the nested structure
      const favoritesArray = Array.isArray(favoritesData) 
        ? favoritesData 
        : (favoritesData as any)?.data || [];

      // Stable-id match: SPARC KPI item_id now uses canonical kpi_code from kpi-metrics-v2.
      const existingFavorite = favoritesArray.find(
        (fav: any) => fav.item_type === 'kpi' && String(fav.item_id) === String(id)
      );

      if (existingFavorite) {
        // Item is already favorited - unfavorite it (PUT request)
        currentOperation = 'remove'; // Set operation type for error handling
        
        const requestBody = isEditMode ? {
          standup_id: String(standupId),
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'kpi',
          item_id: String(id),
          item_name: String(title),
          module_name: String(title),
          is_favourite: false,
          modified_by: String(userName),
        } : {
          standup_session_id: String(sessionId),
          is_favourite: false,
          modified_by: String(userName),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'kpi',
          item_id: String(id),
          item_name: String(title),
          module_name: String(title),
        };

        console.log(isEditMode, 'PUT Santosh /stand-up/favourites to unfavorite with body:', requestBody);

        const response = await apiClient(api, '/stand-up/favourites', 'PUT', requestBody);

        if (response?.success) {
          console.log('Successfully removed SPARC KPI from favorites');
          
          if (!options?.skipRefresh) {
            console.log('Fetching updated favorites after removing SPARC KPI...');
            await refreshFavoritesList();
          }
          
          return true;
        } else {
          console.error('Failed to remove SPARC KPI from favorites:', response);
          // Use centralized error handler to show overlay/modal
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.REMOVE_FAVORITE,
            new Error(response?.message || 'Unable to remove favorite'),
            true, // displayModal = true → show overlay
            { id, title, action: 'remove_favorite' }
          );
          return false;
        }
      } else {
        // Item is not favorited - add it (POST request)
        currentOperation = 'add'; // Set operation type for error handling
        
        const requestBody = isEditMode ? {
          standup_id: String(standupId),
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'kpi',
          item_id: String(id),
          item_name: String(title),
          created_by: String(userName),
        } : {
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'kpi',
          item_id: String(id),
          item_name: String(title),
          created_by: String(userName),
        };

        const response = await apiClient(api, '/stand-up/favourites', 'POST', requestBody);

        if (response?.success) {
          console.log('Successfully added SPARC KPI to favorites');
          
          if (!options?.skipRefresh) {
            console.log('Fetching updated favorites after adding SPARC KPI...');
            await refreshFavoritesList();
          }
          
          return true;
        } else {
          console.error('Failed to add SPARC KPI to favorites:', response);
          // Use centralized error handler to show overlay/modal
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.ADD_FAVORITE,
            new Error(response?.message || 'Unable to add favorite'),
            true, // displayModal = true → show overlay
            { id, title, action: 'add_favorite' }
          );
          return false;
        }
      }
    } catch (error) {
      console.error('Error toggling SPARC KPI favorite:', error);
      // Use the tracked operation type to show correct error message
      const apiName = currentOperation === 'remove' ? STANDUP_API_NAMES.REMOVE_FAVORITE : STANDUP_API_NAMES.ADD_FAVORITE;
      
      // Use centralized error handler for network/unexpected errors
      handleStandUpErrorWithFriendlyMessage(
        apiName,
        error,
        true, // displayModal = true → show overlay
        { title, action: `${currentOperation}_favorite` }
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Get lighthouse toggle hook if params provided
  const { toggleFavorite: toggleLighthouseFavorite } = useLighthouseKpiFavorite();

  // Helper function to check if a SPARC KPI is favorited
  const isSparcKpiFavorited = useCallback((cardId: string): boolean => {
    const favoritesArray = Array.isArray(favoritesData) 
      ? favoritesData 
      : (favoritesData as any)?.data || [];
    
    return favoritesArray.some(
      (fav: any) => fav.item_type === 'kpi' && String(fav.item_id) === String(cardId) && fav.is_favourite === true
    );
  }, [favoritesData]);

  // Helper function to check if a Lighthouse KPI is favorited
  const isLighthouseKpiFavorited = useCallback((cardTitle: string): boolean => {
    const favoritesArray = Array.isArray(favoritesData) 
      ? favoritesData 
      : (favoritesData as any)?.data || [];
    
    return favoritesArray.some(
      (fav: any) => fav.item_type === 'lighthouse_metric' && fav.item_name === cardTitle && fav.is_favourite === true
    );
  }, [favoritesData]);

  // Modal handlers - only if params provided
  const handleSparcToggleFavorite = useCallback((cardId: string) => {
    if (!params) return;

    const card = params.sparcCards.find(c => c.id === cardId);
    if (!card) return;

    const isFavorited = isSparcKpiFavorited(card.id);

    if (!isFavorited) {
      // Adding favorite - immediate action
      params.onToggleFavorite(cardId);
    } else {
      // Removing favorite - show confirmation modal
      params.setPendingUnfavouriteCardId(cardId);
      params.setPendingUnfavouriteCardTitle(card.title);
      params.setPendingUnfavouriteType('sparc');
      params.setShowUnfavouriteModal(true);
    }
  }, [params, isSparcKpiFavorited]);

  const handleLighthouseToggleFavorite = useCallback((cardId: string) => {
    if (!params) return;

    const card = params.lighthouseCardsData.find(c => c.id === cardId);
    if (!card) return;

    const isFavorited = isLighthouseKpiFavorited(card.title);

    if (!isFavorited) {
      // Adding favorite - immediate action
      if (params.onToggleLighthouseFavorite) {
        params.onToggleLighthouseFavorite(cardId);
      }
    } else {
      // Removing favorite - show confirmation modal
      params.setPendingUnfavouriteCardId(cardId);
      params.setPendingUnfavouriteCardTitle(card.title);
      params.setPendingUnfavouriteType('lighthouse');
      params.setShowUnfavouriteModal(true);
    }
  }, [params, isLighthouseKpiFavorited]);

  const handleConfirmUnfavourite = useCallback(async () => {
    if (!params || !params.pendingUnfavouriteCardId || !params.pendingUnfavouriteType || !params.pendingUnfavouriteCardTitle) return;

    // Call API hook to unfavourite
    if (params.pendingUnfavouriteType === 'sparc') {
      await toggleFavorite(params.pendingUnfavouriteCardId, params.pendingUnfavouriteCardTitle);
    } else if (params.pendingUnfavouriteType === 'lighthouse') {
      await toggleLighthouseFavorite(params.pendingUnfavouriteCardTitle);
    }

    // Close modal and reset
    params.setShowUnfavouriteModal(false);
    params.setPendingUnfavouriteCardId(null);
    params.setPendingUnfavouriteCardTitle(null);
    params.setPendingUnfavouriteType(null);
  }, [params, toggleFavorite, toggleLighthouseFavorite]);

  const handleCancelUnfavourite = useCallback(() => {
    if (!params) return;

    params.setShowUnfavouriteModal(false);
    params.setPendingUnfavouriteCardId(null);
    params.setPendingUnfavouriteCardTitle(null);
    params.setPendingUnfavouriteType(null);
  }, [params]);

  return {
    toggleFavorite,
    refreshFavoritesList,
    isLoading,
    isSparcKpiFavorited,
    isLighthouseKpiFavorited,
    handleSparcToggleFavorite,
    handleLighthouseToggleFavorite,
    handleConfirmUnfavourite,
    handleCancelUnfavourite,
  };
};
